import type { TimelineItem } from '@/services/api/timeline'
import type { GeoAnchor, GeoTimeEvent, GeoTrack, InvestigationGeo } from '@/types/investigation'
import type { SnippetTime, SourceSnippet } from '@/types/source-snippet'
import { snippetNodeId } from './object-set'

function rowLabel(snippet: SourceSnippet, row: number) {
  const column = snippet.bindings.labelColumn
  const value = column ? snippet.snapshot.result.rows[row]?.[column] : null
  return value == null || value === '' ? `${snippet.title} · row ${row + 1}` : String(value)
}

/** Day-only dates are axis anchors with explicit day precision, never local instants. */
function axisTime(time: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(time) ? `${time}T00:00:00Z` : time
}

function timelineItem(snippet: SourceSnippet, time: SnippetTime): TimelineItem {
  const label = rowLabel(snippet, time.row)
  const base = {
    id: time.id,
    source_kind: 'snippet' as const,
    source_type: 'SourceSnippet',
    source_id: `${snippetNodeId(snippet.id)}:row:${time.row}`,
    source_label: label,
    event_kind: `${snippet.id}:${time.aspect}`,
    event_label: time.label,
    participants: { primary: { id: time.id, label, type: 'SourceSnippet' }, others: [] },
    display_props: { ...snippet.snapshot.result.rows[time.row], precision: time.precision },
  }
  return time.kind === 'interval'
    ? {
        ...base,
        kind: 'interval',
        start: axisTime(time.start),
        end: time.end ? axisTime(time.end) : null,
      }
    : { ...base, kind: 'point', at: axisTime(time.start) }
}

export function snippetTimelineItems(snippets: readonly SourceSnippet[]): TimelineItem[] {
  return snippets.flatMap(snippet =>
    snippet.projection.times.map(time => timelineItem(snippet, time))
  )
}

function snippetGeo(snippet: SourceSnippet) {
  const times = new Map(snippet.projection.times.map(time => [time.id, time]))
  const anchors: GeoAnchor[] = []
  const events: GeoTimeEvent[] = []
  const tracks = new Map<string, GeoTrack>()
  for (const location of snippet.projection.locations) {
    const time = location.timeId ? times.get(location.timeId) : undefined
    const name = rowLabel(snippet, location.row)
    anchors.push({
      anchor_id: location.id,
      name,
      type: 'SourceSnippet',
      latitude: location.latitude,
      longitude: location.longitude,
      temporal: Boolean(time),
      temporal_min: time ? axisTime(time.start) : null,
      temporal_max: time
        ? time.precision === 'day'
          ? `${(time.end ?? time.start).slice(0, 10)}T23:59:59.999Z`
          : axisTime(time.end ?? time.start)
        : null,
    })
    if (!time) continue
    const timestamp = axisTime(time.start)
    events.push({
      entity_id: location.id,
      entity_name: name,
      type: 'SourceSnippet',
      latitude: location.latitude,
      longitude: location.longitude,
      timestamp,
      location_name: location.label,
    })
    if (!location.track || time.precision !== 'instant') continue
    const id = `${snippetNodeId(snippet.id)}:track:${location.aspect}:${encodeURIComponent(location.track)}`
    const track = tracks.get(id) ?? {
      entity_id: id,
      name: location.track,
      type: 'SourceSnippet',
      points: [],
    }
    track.points.push({ latitude: location.latitude, longitude: location.longitude, timestamp })
    tracks.set(id, track)
  }
  for (const track of tracks.values())
    track.points.sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  return { anchors, events, tracks: [...tracks.values()].filter(track => track.points.length > 1) }
}

export function mergeSnippetGeo(
  base: InvestigationGeo | null,
  snippets: readonly SourceSnippet[]
): InvestigationGeo {
  const result: InvestigationGeo = {
    mode: 'all',
    anchors: [...(base?.anchors ?? [])],
    tracks: [...(base?.tracks ?? [])],
    events: [...(base?.events ?? [])],
  }
  for (const snippet of snippets) {
    const geo = snippetGeo(snippet)
    result.anchors?.push(...geo.anchors)
    result.tracks?.push(...geo.tracks)
    result.events?.push(...geo.events)
  }
  return result
}
